/**
 * Best-effort normalisation of Japanese street addresses entered by hand.
 *
 * Applicants write the same address in many ways ("４丁目３−５", "4丁目3-5",
 * "四丁目3の5"...), which makes the stored data hard to compare and to reuse.
 * This module folds the common variations into the canonical "1-2-3" form.
 *
 * It is deliberately conservative: a rule is applied only when the surrounding
 * context makes the intent unambiguous, because a wrong "correction" of a
 * place name is far worse than leaving an inconsistent one alone. Kanji
 * numerals are converted only when a block marker follows them, and the
 * ambiguous markers (番, 号) additionally require a lookahead, so place names
 * such as 三田 / 四谷 / 一番町 / 八丁堀 / 十日町 are never touched.
 */

const KANJI_DIGITS: Record<string, number> = {
  〇: 0,
  一: 1,
  二: 2,
  三: 3,
  四: 4,
  五: 5,
  六: 6,
  七: 7,
  八: 8,
  九: 9,
};

/** Parse a kanji numeral in the 0-99 range. Returns null if unsupported. */
const parseKanjiNumber = (kanji: string): number | null => {
  if (kanji.length === 1 && kanji in KANJI_DIGITS) return KANJI_DIGITS[kanji];

  const withTens = /^([一二三四五六七八九]?)十([〇一二三四五六七八九]?)$/.exec(kanji);
  if (withTens) {
    const tens = withTens[1] ? KANJI_DIGITS[withTens[1]] : 1;
    const ones = withTens[2] ? KANJI_DIGITS[withTens[2]] : 0;
    return tens * 10 + ones;
  }

  return null;
};

/** Full-width ASCII alphanumerics and the ideographic space to their half-width form. */
const toHalfWidthAlnum = (value: string): string =>
  value
    .replace(/[Ａ-Ｚａ-ｚ０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0))
    .replace(/　/g, ' ');

/**
 * Dash-like characters that can only be hyphens in an address:
 * U+2010..U+2015 (hyphen through horizontal bar), U+2212 (minus sign) and
 * U+FF0D (full-width hyphen-minus).
 */
const UNAMBIGUOUS_DASHES = /[‐-―−－]/g;

/**
 * 'ー' (katakana prolonged sound mark), '〜', '~' and 'の' / 'ノ' also occur
 * inside proper nouns, so they count as separators only between two digits.
 */
const DIGIT_SEPARATORS = /(\d)\s*[ー〜～~のノ之]\s*(?=\d)/g;

const toArabic = (whole: string, kanji: string, marker: string): string => {
  const parsed = parseKanjiNumber(kanji);
  return parsed === null ? whole : `${parsed}${marker}`;
};

/**
 * Convert kanji numerals to arabic where the marker that follows proves the
 * numeral is a block number.
 *
 * Registry addresses (法人番号公表サイト, 登記簿) are commonly written entirely
 * in kanji — 「渋谷一丁目二番三号」 — so 番 and 号 have to be covered as well as
 * 丁目 / 番地. They are ambiguous on their own (一番町, 一号館), hence the
 * lookaheads: 番 must be followed by another number, and 号 must end the
 * address.
 */
const convertKanjiBlockNumbers = (value: string): string =>
  value
    .replace(/([〇一二三四五六七八九十]+)(丁目|番地)/g, toArabic)
    .replace(/([〇一二三四五六七八九十]+)(番)(?=[〇一二三四五六七八九十]|\d)/g, toArabic)
    .replace(/([〇一二三四五六七八九十]+)(号)(?=\s|$)/g, toArabic);

/**
 * Replace the Japanese block markers with hyphens.
 *
 * 番 and 号 are ambiguous (一番町, 1号館) so they are rewritten only when what
 * follows proves they terminate a number: 番 must be followed by a digit, and
 * 号 must end the address.
 */
const replaceBlockMarkers = (value: string): string =>
  value
    .replace(/(\d)\s*丁目/g, '$1-')
    .replace(/(\d)\s*番地/g, '$1-')
    .replace(/(\d)\s*番(?=\s*\d)/g, '$1-')
    .replace(/(\d)\s*号(?=\s|$)/g, '$1');

/**
 * Fold a separator that survived because the marker before it only became a
 * hyphen afterwards, as in 「1丁目2番地の3」 → 「1-2-の3」.
 */
const collapseSeparatorAfterHyphen = (value: string): string =>
  value.replace(/-\s*[ー〜～~のノ之]\s*(?=\d)/g, '-');

/** Tidy up hyphens and whitespace left behind by the substitutions above. */
const tidy = (value: string): string =>
  value
    .replace(/\s*-\s*/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/\s{2,}/g, ' ')
    .trim()
    .replace(/-+$/, '');

/**
 * Normalise a street address (the part up to the house number).
 *
 * Building names and room numbers are collected in a separate field, so this
 * is only ever applied to the numeric part of the address.
 */
export const normalizeAddress = (value: string): string => {
  if (!value) return '';

  const halfWidth = toHalfWidthAlnum(value)
    .replace(UNAMBIGUOUS_DASHES, '-')
    .replace(DIGIT_SEPARATORS, '$1-');

  return tidy(
    collapseSeparatorAfterHyphen(replaceBlockMarkers(convertKanjiBlockNumbers(halfWidth)))
  );
};

/** Collapse whitespace in a building name / room number. */
export const normalizeBuildingName = (value: string): string => {
  if (!value) return '';
  return toHalfWidthAlnum(value)
    .replace(/\s{2,}/g, ' ')
    .trim();
};

/**
 * Compose the single `address` string the API expects from the two input
 * fields. The building name is appended after a single half-width space.
 */
export const joinAddress = (address: string, buildingName: string): string =>
  [normalizeAddress(address), normalizeBuildingName(buildingName)].filter(Boolean).join(' ');

export default normalizeAddress;
